import React from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';

import {
	Button,
	PasswordInputWithMeter,
	ControlledInput,
	Flex,
	Label,
	LayoutShimmer,
	mediaQueries,
} from '@zeal/web-ui';
import { useMediaQuery, sharedSchema } from '@zeal/zeal-lib';

import useResetPassword from '@app/data/ForgotPassword/useResetPassword';

import { EResetStatus, TResetPasswordForm } from '../types';
import { useResetStore } from '@app/store/resetPasswordStore';
import { validatePassword } from '../utils/validatePassword';
import { TFunction } from 'i18next';

const { passwordSchema: passwordSchema } = sharedSchema;

const passwordConfirmationSchema =
	(t: TFunction) =>
	({
		name = t('common:confirm_password'),
		t: translate,
	}: {
		name?: string;
		t?: typeof t;
	}) =>
		yup
			.string()
			.required(
				(translate || t)('common:required_validation', {
					name: name,
				})
			)
			.oneOf(
				[yup.ref('password'), null],
				(translate || t)('common:password_confirmation_validation', {
					name: name,
				})
			);

const ResetPasswordForm = ({ token }: { token: string }) => {
	const { t } = useTranslation('forgotPassword');
	const isDesktop = useMediaQuery(mediaQueries.min.md);
	const navigate = useNavigate();

	const { setResetStatus } = useResetStore();

	const formSchema = yup.object({
		password: passwordSchema({ min: 8 }),
		password_confirmation: passwordConfirmationSchema(t)({}),
		token: yup.string(),
	});

	const formMethods = useForm<TResetPasswordForm>({
		defaultValues: {
			password: '',
			token: '',
			password_confirmation: '',
		},
		resolver: yupResolver(formSchema),
		mode: 'onSubmit',
	});
	const {
		handleSubmit,
		formState: { isDirty },
	} = formMethods;

	const { isError, isPending, doResetPassword, errors } = useResetPassword({
		onSuccess: () => {
			setResetStatus(EResetStatus.CHANGED_SUCCESSFULLY);
		},
		onError: () => {
			setResetStatus(EResetStatus.LINK_USED_BEFORE);
		},
	});

	const handleSubmitNewPassword = (data: TResetPasswordForm) => {
		doResetPassword({
			password: data.password,
			password_confirmation: data.password_confirmation,
			token: token,
		});
	};

	const handleCancel = () => navigate('/');

	return (
		<LayoutShimmer isLoading={isPending}>
			<FormProvider {...formMethods}>
				<Flex isColumn gap="6" align="center" className="h-full">
					<Flex
						gap="12"
						isColumn
						justify={isDesktop ? 'center' : 'start'}
						className="h-full px-4 pt-6 pb-8 md:py-12 md:px-7"
						style={{
							width: isDesktop ? '500px' : '343px',
						}}
					>
						<Flex gap="2" isColumn>
							<Label.Big600 textAlign="start">
								{t('reset_password')}
							</Label.Big600>
							<Label.Thin300 textAlign="start" variant="subdued">
								{t('reset_password_subtitle')}
							</Label.Thin300>
						</Flex>

						<Flex gap="6" isColumn>
							<PasswordInputWithMeter<TResetPasswordForm>
								name={'password'}
								inputProps={{
									name: 'password',
									label: <Label.Mid300>{t('enter_new_password')}</Label.Mid300>,
									placeholder: t('password_placeholder'),
								}}
								rules={{
									validate: validatePassword,
								}}
								isError={Boolean(errors?.errors?.password)}
								passwordStrengthText={t('password_strength')}
								errorMsg={t('password_error_message')}
							/>
							<ControlledInput<TResetPasswordForm>
								name="password_confirmation"
								label={t('confirm_new_password')}
								type="password"
								className="w-full"
								placeholder={t('Enter a password')}
								isError={Boolean(errors?.errors?.password_confirmation)}
								message={t(errors?.errors?.password_confirmation) as string}
							/>
						</Flex>
						<Flex gap="4" isColumn align="center">
							<Button
								variant="primary"
								size="lg"
								className="w-full"
								isLoading={isPending}
								onClick={handleSubmit(handleSubmitNewPassword)}
								disabled={!isDirty || isError}
							>
								{t('reset_password')}
							</Button>
							<Button
								variant="ghost"
								size="lg"
								className="w-full"
								isLoading={isPending}
								onClick={handleCancel}
							>
								{t('back_to_login')}
							</Button>

							<Label.Big300>
								{t('need_help')}
								<Label.Thin300>{t('email_us_at')}</Label.Thin300> &nbsp;
								<a href="mailto:support@getzeal.io">
									<Label.Mid300
										variant="interactive"
										className="hover:text-text-interactive"
									>
										support@getzeal.io
									</Label.Mid300>
								</a>
							</Label.Big300>
						</Flex>
					</Flex>
				</Flex>
			</FormProvider>
		</LayoutShimmer>
	);
};

export default ResetPasswordForm;
